import { apiFetch } from './http';

export class EventApiError extends Error {
  code: string | null;
  status: number;

  constructor(message: string, options: { code?: string | null; status: number }) {
    super(message);
    this.name = 'EventApiError';
    this.code = options.code ?? null;
    this.status = options.status;
  }
}

async function readEventApiError(res: Response, fallback: string): Promise<{ message: string; code: string | null }> {
  const text = await res.text();
  if (!text.trim()) return { message: fallback, code: null };
  try {
    const data = JSON.parse(text) as { error?: unknown; detail?: unknown; message?: unknown };
    for (const value of [data.message, data.detail, data.error]) {
      if (typeof value === 'string' && value.trim()) {
        return {
          message: value,
          code: typeof data.error === 'string' && data.error.trim() ? data.error : null,
        };
      }
    }
  } catch {
    // Keep the raw text fallback for non-JSON server responses.
  }
  return { message: text, code: null };
}

/**
 * Revive an event from Lost or Cancelled back to quote. Logs to event_history and
 * notifies the current user's manager with a link to the event.
 */
export async function reviveEvent(eventId: string, reason: string): Promise<{ ok: boolean; status: string } | { error: string }> {
  const res = await apiFetch(`/events/${eventId}/revive`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason.trim() }),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = 'Failed to revive event';
    try {
      const body = JSON.parse(text) as { error?: string };
      if (body?.error) message = body.error;
    } catch {
      if (text) message = text;
    }
    return { error: message };
  }
  const data = (await res.json()) as { ok?: boolean; status?: string };
  return { ok: true, status: data?.status ?? 'quote' };
}

export type EventReservationJobSummary = {
  status: 'pending' | 'processing' | 'completed' | 'failed' | string;
  insertedTotal: number;
  remainingCount: number | null;
  lastError: string | null;
  failureKind: string | null;
  retryable: boolean;
  lastOutcome: string | null;
  updatedAt: string | null;
  source: string | null;
  attempts: number;
  requestedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  leaseExpiresAt: string | null;
};

export const EVENT_CONFIRMATION_POLICIES = [
  'client_signature_required',
  'direct_bypass_allowed',
  'manager_bypass_required',
] as const;

export type EventConfirmationPolicy = typeof EVENT_CONFIRMATION_POLICIES[number];

export type EventConfirmationRecord = {
  recordId: string | null;
  method: string | null;
  status: string | null;
  isCurrent: boolean;
  policy: EventConfirmationPolicy | null;
  recordedAt: string | null;
  confirmedAt: string | null;
  confirmedByProfileId: string | null;
  confirmedByName: string | null;
  clientAcceptedAt: string | null;
  acceptedClientEmail: string | null;
  signatureMethod: string | null;
  reason: string | null;
  requestedAt: string | null;
  requestedByProfileId: string | null;
  requestedByName: string | null;
  approvedAt: string | null;
  approvedByProfileId: string | null;
  approvedByName: string | null;
  eventOrderAcceptanceId: string | null;
  acceptanceId: string | null;
  signatureRecordId: string | null;
  requestId: string | null;
  quoteVersionNumber: number | null;
  quoteSnapshotVersion: string | null;
  quoteSnapshotHash: string | null;
  r2ConvertedAt: string | null;
  importedAt: string | null;
  sourceTimestampQuality: string | null;
  sourceTimezone: string | null;
  sourceId: string | null;
  inventoryCommitmentSeq: string | null;
};

export type EventConfirmationPendingRequest = {
  requestId: string | null;
  status: string | null;
  requestedAt: string | null;
  requestedByProfileId: string | null;
  requestedByName: string | null;
  reason: string | null;
  canCurrentUserDecide: boolean;
};

export type EventConfirmationState = {
  eventId: string;
  eventStatus: string | null;
  locationId: string | null;
  policy: EventConfirmationPolicy;
  clientSignaturePresent: boolean;
  requiresManagerApproval: boolean;
  latestRecord: EventConfirmationRecord | null;
  pendingRequest: EventConfirmationPendingRequest | null;
  canRequest: boolean;
};

export type ConfirmEventInput = {
  reason: string;
  acknowledgeNoClientSignature: true;
};

export type ConfirmEventResult = {
  eventId: string;
  status: string;
  reservationJobStatus: string | null;
  reservationJob?: EventReservationJobSummary;
  confirmation: EventConfirmationState;
  requested: boolean;
};

type UnknownRecord = Record<string, unknown>;

function asRecord(value: unknown): UnknownRecord | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? value as UnknownRecord
    : null;
}

function firstValue(record: UnknownRecord | null, keys: string[]): unknown {
  if (!record) return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(record, key)) return record[key];
  }
  return undefined;
}

function stringValue(record: UnknownRecord | null, keys: string[]): string | null {
  const value = firstValue(record, keys);
  if (typeof value === 'string') return value.trim() || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function numberValue(record: UnknownRecord | null, keys: string[]): number | null {
  const value = firstValue(record, keys);
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function booleanValue(record: UnknownRecord | null, keys: string[], fallback = false): boolean {
  const value = firstValue(record, keys);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
    if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  }
  return fallback;
}

function normalizeReservationJobSummary(value: unknown): EventReservationJobSummary | null {
  const root = asRecord(value);
  if (!root) return null;

  const nestedValue = firstValue(root, ['reservationJob', 'reservation_job']);
  if (
    (Object.prototype.hasOwnProperty.call(root, 'reservationJob') && nestedValue == null)
    || (Object.prototype.hasOwnProperty.call(root, 'reservation_job') && nestedValue == null)
    || booleanValue(root, ['hasJob', 'has_job'], true) === false
  ) {
    return null;
  }

  const record = asRecord(nestedValue) ?? root;
  const status = stringValue(record, ['status']);
  if (!status) return null;

  const summary: EventReservationJobSummary = {
    status,
    insertedTotal: numberValue(record, ['insertedTotal', 'inserted_total']) ?? 0,
    remainingCount: numberValue(record, ['remainingCount', 'remaining_count']),
    lastError: stringValue(record, ['lastError', 'last_error']),
    failureKind: stringValue(record, ['failureKind', 'failure_kind']),
    retryable: booleanValue(record, ['retryable']),
    lastOutcome: stringValue(record, ['lastOutcome', 'last_outcome']),
    updatedAt: stringValue(record, ['updatedAt', 'updated_at']),
    source: stringValue(record, ['source']),
    attempts: numberValue(record, ['attempts']) ?? 0,
    requestedAt: stringValue(record, ['requestedAt', 'requested_at']),
    startedAt: stringValue(record, ['startedAt', 'started_at']),
    finishedAt: stringValue(record, ['finishedAt', 'finished_at']),
    leaseExpiresAt: stringValue(record, ['leaseExpiresAt', 'lease_expires_at']),
  };

  // Older servers represented "no job exists" as a synthetic pending object.
  // A real persisted job always has at least one lifecycle timestamp.
  if (
    status.toLowerCase() === 'pending'
    && summary.insertedTotal === 0
    && summary.remainingCount == null
    && !summary.lastError
    && !summary.updatedAt
    && !summary.source
    && !summary.requestedAt
    && !summary.startedAt
    && !summary.finishedAt
  ) {
    return null;
  }

  return summary;
}

function normalizeConfirmationPolicy(value: unknown): EventConfirmationPolicy {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'direct_bypass_allowed') return 'direct_bypass_allowed';
  if (normalized === 'manager_bypass_required') return 'manager_bypass_required';
  return 'client_signature_required';
}

function normalizeConfirmationRecord(value: unknown): EventConfirmationRecord | null {
  const record = asRecord(value);
  if (!record) return null;
  return {
    recordId: stringValue(record, [
      'recordId',
      'record_id',
      'confirmationRecordId',
      'confirmation_record_id',
      'id',
    ]),
    method: stringValue(record, ['method', 'confirmationMethod', 'confirmation_method', 'source']),
    status: stringValue(record, ['status']),
    isCurrent: booleanValue(record, ['isCurrent', 'is_current'], false),
    policy: (() => {
      const value = firstValue(record, ['policy', 'eventConfirmationPolicy', 'event_confirmation_policy']);
      return value == null ? null : normalizeConfirmationPolicy(value);
    })(),
    recordedAt: stringValue(record, ['recordedAt', 'recorded_at', 'createdAt', 'created_at']),
    confirmedAt: stringValue(record, ['confirmedAt', 'confirmed_at', 'finalizedAt', 'finalized_at']),
    confirmedByProfileId: stringValue(record, [
      'confirmedByProfileId',
      'confirmed_by_profile_id',
      'confirmedBy',
      'confirmed_by',
      'finalized_by',
    ]),
    confirmedByName: stringValue(record, [
      'confirmedByName',
      'confirmed_by_name',
      'confirmedByDisplayName',
      'confirmed_by_display_name',
    ]),
    clientAcceptedAt: stringValue(record, [
      'clientAcceptedAt',
      'client_accepted_at',
      'acceptedAt',
      'accepted_at',
    ]),
    acceptedClientEmail: stringValue(record, [
      'acceptedClientEmail',
      'accepted_client_email',
      'clientEmail',
      'client_email',
    ]),
    signatureMethod: stringValue(record, ['signatureMethod', 'signature_method']),
    reason: stringValue(record, ['reason', 'confirmationReason', 'confirmation_reason', 'decision_note']),
    requestedAt: stringValue(record, ['requestedAt', 'requested_at']),
    requestedByProfileId: stringValue(record, ['requestedByProfileId', 'requested_by_profile_id', 'requestedBy', 'requested_by']),
    requestedByName: stringValue(record, ['requestedByName', 'requested_by_name', 'requesterName', 'requester_name']),
    approvedAt: stringValue(record, ['approvedAt', 'approved_at', 'decidedAt', 'decided_at']),
    approvedByProfileId: stringValue(record, ['approvedByProfileId', 'approved_by_profile_id', 'approvedBy', 'approved_by', 'decided_by']),
    approvedByName: stringValue(record, ['approvedByName', 'approved_by_name', 'decided_by_name']),
    eventOrderAcceptanceId: stringValue(record, [
      'eventOrderAcceptanceId',
      'event_order_acceptance_id',
      'acceptanceId',
      'acceptance_id',
    ]),
    acceptanceId: stringValue(record, ['acceptanceId', 'acceptance_id', 'eventOrderAcceptanceId', 'event_order_acceptance_id']),
    signatureRecordId: stringValue(record, ['signatureRecordId', 'signature_record_id']),
    requestId: stringValue(record, ['requestId', 'request_id']),
    quoteVersionNumber: numberValue(record, [
      'quoteVersionNumber',
      'quote_version_number',
      'acceptedVersionNumber',
      'accepted_version_number',
    ]),
    quoteSnapshotVersion: stringValue(record, [
      'quoteSnapshotVersion',
      'quote_snapshot_version',
      'snapshotSchemaVersion',
      'snapshot_schema_version',
    ]),
    quoteSnapshotHash: stringValue(record, [
      'quoteSnapshotHash',
      'quote_snapshot_hash',
      'acceptedQuoteSnapshotHash',
      'accepted_quote_snapshot_hash',
    ]),
    r2ConvertedAt: stringValue(record, ['r2ConvertedAt', 'r2_converted_at', 'sourceCommittedAt', 'source_committed_at']),
    importedAt: stringValue(record, [
      'importedAt',
      'imported_at',
      'recordedAt',
      'recorded_at',
      'created_at',
    ]),
    sourceTimestampQuality: stringValue(record, ['sourceTimestampQuality', 'source_timestamp_quality']),
    sourceTimezone: stringValue(record, ['sourceTimezone', 'source_timezone']),
    sourceId: stringValue(record, ['sourceId', 'source_id', 'source_record_id']),
    // Commitment keys are timestamp-derived bigint values and exceed
    // JavaScript's safe integer range. Preserve the exact database value.
    inventoryCommitmentSeq: stringValue(record, ['inventoryCommitmentSeq', 'inventory_commitment_seq']),
  };
}

function normalizePendingRequest(value: unknown): EventConfirmationPendingRequest | null {
  const record = asRecord(value);
  if (!record) return null;
  return {
    requestId: stringValue(record, ['requestId', 'request_id', 'id', 'approval_id']),
    status: stringValue(record, ['status']),
    requestedAt: stringValue(record, ['requestedAt', 'requested_at', 'created_at']),
    requestedByProfileId: stringValue(record, ['requestedByProfileId', 'requested_by_profile_id', 'requestedBy', 'requested_by']),
    requestedByName: stringValue(record, [
      'requestedByName',
      'requested_by_name',
      'requesterName',
      'requester_name',
    ]),
    reason: stringValue(record, ['reason', 'request_reason', 'decision_note']),
    canCurrentUserDecide: booleanValue(record, ['canCurrentUserDecide', 'can_current_user_decide'], false),
  };
}

export function normalizeEventConfirmationState(
  value: unknown,
  fallbackEventId = '',
): EventConfirmationState {
  const root = asRecord(value);
  const nested = asRecord(firstValue(root, ['confirmation', 'confirmationState', 'confirmation_state']));
  const record = nested ?? root;
  const policy = normalizeConfirmationPolicy(firstValue(record, [
    'policy',
    'eventConfirmationPolicy',
    'event_confirmation_policy',
  ]));
  return {
    eventId: stringValue(record, ['eventId', 'event_id']) ?? fallbackEventId,
    eventStatus: stringValue(record, ['eventStatus', 'event_status', 'status']),
    locationId: stringValue(record, ['locationId', 'location_id']),
    policy,
    clientSignaturePresent: booleanValue(record, ['clientSignaturePresent', 'client_signature_present'], false),
    requiresManagerApproval: booleanValue(
      record,
      ['requiresManagerApproval', 'requires_manager_approval'],
      policy === 'manager_bypass_required',
    ),
    latestRecord: normalizeConfirmationRecord(firstValue(record, [
      'latestRecord',
      'latest_record',
      'confirmationRecord',
      'confirmation_record',
    ])),
    pendingRequest: normalizePendingRequest(firstValue(record, [
      'pendingRequest',
      'pending_request',
      'confirmationRequest',
      'confirmation_request',
    ])),
    canRequest: booleanValue(record, ['canRequest', 'can_request'], false),
  };
}

export async function fetchEventConfirmation(eventId: string): Promise<EventConfirmationState> {
  const res = await apiFetch(`/events/${eventId}/confirmation`);
  if (!res.ok) {
    const error = await readEventApiError(res, 'Failed to load event confirmation details');
    throw new EventApiError(error.message, { code: error.code, status: res.status });
  }
  const data = await res.json() as unknown;
  return normalizeEventConfirmationState(data, eventId);
}

export async function confirmEvent(eventId: string, input: ConfirmEventInput): Promise<ConfirmEventResult> {
  const reason = input.reason.trim();
  if (!reason) {
    throw new EventApiError('A reason is required to confirm without a client signature.', {
      code: 'confirmation_reason_required',
      status: 400,
    });
  }
  const res = await apiFetch(`/events/${eventId}/confirm`, {
    method: 'POST',
    body: JSON.stringify({
      reason,
      acknowledgeNoClientSignature: input.acknowledgeNoClientSignature === true,
    }),
  });
  if (!res.ok) {
    const error = await readEventApiError(res, 'Failed to confirm event');
    throw new EventApiError(error.message, { code: error.code, status: res.status });
  }
  const data = await res.json() as UnknownRecord;
  const confirmation = normalizeEventConfirmationState(data, eventId);
  const normalizedReservationJob = normalizeReservationJobSummary(
    data.reservationJob ?? data.reservation_job,
  ) ?? undefined;
  return {
    eventId: stringValue(data, ['eventId', 'event_id']) ?? confirmation.eventId ?? eventId,
    status: stringValue(data, ['status', 'eventStatus', 'event_status'])
      ?? confirmation.eventStatus
      ?? 'confirmed',
    reservationJobStatus: stringValue(data, ['reservationJobStatus', 'reservation_job_status'])
      ?? normalizedReservationJob?.status
      ?? null,
    reservationJob: normalizedReservationJob,
    confirmation,
    requested: booleanValue(data, ['requested', 'approvalRequested', 'approval_requested'], Boolean(confirmation.pendingRequest)),
  };
}

export async function fetchEventReservationJob(eventId: string): Promise<EventReservationJobSummary | null> {
  const res = await apiFetch(`/events/${eventId}/reservation-job`);
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to load reservation job status');
  }
  const data = await res.json() as unknown;
  return normalizeReservationJobSummary(data);
}

export async function processEventReservationJob(
  eventId: string,
  options?: { maxJobs?: number; batchSize?: number; maxBatchesPerJob?: number },
): Promise<{ ok: boolean; reservationJob: EventReservationJobSummary | null }> {
  const res = await apiFetch(`/events/${eventId}/reservation-job/process`, {
    method: 'POST',
    body: JSON.stringify({
      maxJobs: options?.maxJobs,
      batchSize: options?.batchSize,
      maxBatchesPerJob: options?.maxBatchesPerJob,
    }),
  });
  if (!res.ok) {
    const failure = await readEventApiError(res, 'Failed to process reservation job');
    throw new EventApiError(failure.message, {
      code: failure.code,
      status: res.status,
    });
  }
  const data = await res.json() as UnknownRecord;
  return {
    ok: booleanValue(data, ['ok'], true),
    reservationJob: normalizeReservationJobSummary(
      data.reservationJob ?? data.reservation_job,
    ),
  };
}

export type EventLogisticsDistanceSnapshot = {
  distanceMiles: number | null;
  driveMinutes: number | null;
  source: string | null;
  computedAt: string | null;
  routeKey: string | null;
  cached: boolean;
};

export async function refreshEventLogisticsDistance(params: {
  eventId: string;
  warehouseLocationId: string;
  eventVenueId: string;
  force?: boolean;
}): Promise<EventLogisticsDistanceSnapshot> {
  const res = await apiFetch(`/events/${params.eventId}/logistics-distance/refresh`, {
    method: 'POST',
    body: JSON.stringify({
      warehouseLocationId: params.warehouseLocationId,
      eventVenueId: params.eventVenueId,
      force: params.force === true,
    }),
  });
  if (!res.ok) {
    const error = await readEventApiError(res, 'Failed to refresh logistics distance');
    throw new EventApiError(error.message, { code: error.code, status: res.status });
  }
  const data = (await res.json()) as {
    distanceMiles?: number | null;
    driveMinutes?: number | null;
    source?: string | null;
    computedAt?: string | null;
    routeKey?: string | null;
    cached?: boolean;
  };
  return {
    distanceMiles: data.distanceMiles ?? null,
    driveMinutes: data.driveMinutes ?? null,
    source: data.source ?? null,
    computedAt: data.computedAt ?? null,
    routeKey: data.routeKey ?? null,
    cached: Boolean(data.cached),
  };
}
